import { isDefaultPin } from "@ascleo/frontdesk-shared";
import { isSettingsPinFormValue } from "@ascleo/frontdesk-shared/settings-pin-input";
import { Schema } from "effect";

/**
 * Validation schema for the browser kiosk's device-configuration form.
 *
 * Reuses the shared PIN rule: four to eight digits and never the default `0000`
 * (ITSiRL §75b SGB V), so the browser kiosk can no longer persist a PIN its own unlock screen
 * refuses. A configured PIN is edited through the redaction mask, which stays valid.
 *
 * @since 1.1.7
 * @example
 * ```ts
 * Schema.decodeUnknownSync(WebSettingsFormSchema)({ practiceName: "Praxis", deviceName: "", welcomeMessage: "", selectedTerminalId: "", pin: "4826", voiceOverVolume: 50, schedulerEnabled: true, prescriptionEnabled: false })
 * ```
 */
export const WebSettingsFormSchema = Schema.Struct({
  practiceName: Schema.String,
  deviceName: Schema.String,
  welcomeMessage: Schema.String,
  selectedTerminalId: Schema.String,
  pin: Schema.String.pipe(
    Schema.check(Schema.isMinLength(4, { message: "Die PIN muss mindestens 4 Zeichen haben." })),
    Schema.check(Schema.makeFilter(isSettingsPinFormValue, { message: "Die PIN muss aus 4 bis 8 Ziffern bestehen." })),
    Schema.check(Schema.makeFilter((pin) => !isDefaultPin(pin), {
      message: "Die Standard-PIN 0000 ist nicht erlaubt. Bitte eine eigene PIN wählen.",
    })),
  ),
  languageSelectionOnSessionStart: Schema.optional(Schema.Boolean),
  voiceOverVolume: Schema.Number.pipe(Schema.check(Schema.isInt()), Schema.check(Schema.isBetween({ minimum: 0, maximum: 100 }))),
  schedulerEnabled: Schema.Boolean,
  prescriptionEnabled: Schema.Boolean,
});

/**
 * Decoded browser kiosk device-configuration form values.
 *
 * @since 1.1.7
 * @example
 * ```ts
 * const values: WebSettingsFormData = { practiceName: "", deviceName: "", welcomeMessage: "", selectedTerminalId: "", pin: "••••", voiceOverVolume: 50, schedulerEnabled: true, prescriptionEnabled: false };
 * ```
 */
export type WebSettingsFormData = Schema.Schema.Type<typeof WebSettingsFormSchema>;
